import { enumify, isDate, isNumber, isObjectLike, isOptional, isString } from '../../lib/typeInference.js';

// FIXME given that most metric calcs want to be relative to local time, I can't see how FixedPeriod can be useful
export interface FixedPeriod {
    gt: Date;
    lt?: Date;
}

interface FixedPeriodNamed extends FixedPeriod {
    name: string;
}

// FIXME RollingWindow cannot define periods that take into account daylight savings time changes since the spec
// uses minutes.  Luxon only adjusts for DST for when computing using higher order units like days and greater.  Therefore,
// Window will use fixed timezone offset in its calc
export interface RollingWindowPeriod {
    windowStartOffsetMins: number;
    windowEndOffsetMins?: number;
    gt?: Date;
    lt?: Date;
}

interface RollingWindowPeriodNamed extends RollingWindowPeriod {
    name: string;
}

export const namedTimePeriodValues = [
    'Today',
    'Yesterday',
    'WeekToDate',
    'LastWeek',
    'MonthToDate',
    'LastMonth',
    'QuarterToDate',
    'LastQuarter',
    'YearToDate',
    'LastYear'
] as const;

export type NamedTimePeriod = (typeof namedTimePeriodValues)[number];

export const NamedTimePeriodEnum = enumify(namedTimePeriodValues);

// we need this to stay compatible with v1 UserMetricDefintion
export type Period = NamedTimePeriod | FixedPeriod | RollingWindowPeriod;

export type PeriodNamed = NamedTimePeriod | FixedPeriodNamed | RollingWindowPeriodNamed;

export function isFixedPeriod(obj: any): obj is FixedPeriod {
    return obj && isDate(obj.gt) && isOptional(isDate, obj.lt);
}

export function isRollingWindowPeriod(obj: any): obj is RollingWindowPeriod {
    return obj && isNumber(obj.windowStartOffsetMins) && isOptional(isNumber, obj.windowEndOffsetMins) && isOptional(isDate, obj.gt) && isOptional(isDate, obj.lt);
}

export function isPeriod(obj: any): obj is Period {
    return namedTimePeriodValues.includes(obj) || isFixedPeriod(obj) || isRollingWindowPeriod(obj);
}

export function isPeriodNamed(obj: unknown): obj is PeriodNamed {
    return namedTimePeriodValues.includes(obj as any) || (isObjectLike(obj) && isString(obj.name) && (isFixedPeriod(obj) || isRollingWindowPeriod(obj)));
}

export type RelativeTimeStringUnit = 'seconds' | 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years';
export function isRelativeTimeStringUnit(unit: any): unit is RelativeTimeStringUnit {
    return ['seconds', 'minutes', 'hours', 'days', 'weeks', 'months', 'years'].includes(unit);
}

export type RelativeTimeStringFunction =
    | `StartOfDay:${_RelativeTimeString}`
    | `StartOfWeek:${_RelativeTimeString}`
    | `StartOfMonth:${_RelativeTimeString}`
    | `StartOfYear:${_RelativeTimeString}`
    | `EndOfDay:${_RelativeTimeString}`
    | `EndOfWeek:${_RelativeTimeString}`
    | `EndOfMonth:${_RelativeTimeString}`
    | `EndOfYear:${_RelativeTimeString}`;

type NamedTime = 'NextDay' | 'NextWeek' | 'NextMonth' | 'NextYear';

type _RelativeTimeString = `${'-' | '+'}${number}${RelativeTimeStringUnit}` | `${'-' | '+'}${number}${RelativeTimeStringUnit}@${string}:${string}` | NamedTime;

export type RelativeTimeString = _RelativeTimeString | RelativeTimeStringFunction;

export const relativeTimeStringRegex =
    /^(?:(StartOfDay|StartOfWeek|StartOfMonth|StartOfYear|EndOfDay|EndOfWeek|EndOfMonth|EndOfYear)(?::))?(?:([+-])([0-9]+)(seconds|minutes|hours|days|weeks|months|years)(?:@(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9]))?|(NextDay|NextWeek|NextMonth|NextYear))$/;

export function validateRelativeTimeString(str: string): str is RelativeTimeString {
    return relativeTimeStringRegex.test(str);
}

export function isRelativeTimeString(obj: unknown): obj is RelativeTimeString {
    return typeof obj === 'string' && validateRelativeTimeString(obj);
}
